import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { CircularProgress } from "@mui/material";
import { Toast } from "../Toast";
import { strings } from "../../utils/constants";
import { fetchVideoPlayUrlAction } from "../../redux/Actions";
import "./VideoManagement.css";

/**
 * Plays an existing video using a temporary signed GET URL fetched from the
 * backend on open. The URL is held only in local component state for the
 * duration of the playback session (never persisted). If the signed URL
 * expires and playback errors, a fresh URL is requested once.
 */
export default function VideoPlayerModal({ open, video, onClose }) {
  const dispatch = useDispatch();
  const [playUrl, setPlayUrl] = useState("");
  const [loading, setLoading] = useState(false);
  // Which language's video is being previewed ("en" | "fr").
  const [videoLang, setVideoLang] = useState(strings.language.EN);
  const retriedRef = useRef(false);

  const hasFrVideo = !!(
    video?.hasFrVideo ||
    video?.fileNameFr ||
    video?.file_name_fr
  );

  const loadPlayUrl = async (lang = videoLang) => {
    if (!video?.id) return;
    setLoading(true);
    const res = await dispatch(
      fetchVideoPlayUrlAction({ id: video.id, videoLang: lang })
    );
    setLoading(false);
    if (res.payload?.playUrl) {
      setPlayUrl(res.payload.playUrl);
    } else {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message:
          res.payload?.message || strings.admin.error.VIDEO_PLAY_URL_FAILED,
      });
      onClose?.();
    }
  };

  useEffect(() => {
    if (open) {
      retriedRef.current = false;
      setPlayUrl("");
      setVideoLang(strings.language.EN);
      loadPlayUrl(strings.language.EN);
    } else {
      // Drop the signed URL when the session ends.
      setPlayUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, video?.id]);

  const switchLang = (lang) => {
    if (lang === videoLang) return;
    retriedRef.current = false;
    setVideoLang(lang);
    setPlayUrl("");
    loadPlayUrl(lang);
  };

  // Handle expired signed URLs: retry once with a fresh URL.
  const handleVideoError = (e) => {
    // eslint-disable-next-line no-console
    console.error(
      "[VideoPlayback] <video> failed to load. This is usually S3 CORS/expiry. src:",
      playUrl,
      e?.target?.error
    );
    if (!retriedRef.current) {
      retriedRef.current = true;
      loadPlayUrl();
    } else {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.VIDEO_PLAY_URL_FAILED,
      });
    }
  };

  const handleClose = () => {
    setPlayUrl("");
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ color: "#184437" }}>
        {video?.title || strings.video.PLAY_VIDEO}
      </DialogTitle>
      <DialogContent>
        {hasFrVideo && (
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <Button
              size="small"
              variant={
                videoLang === strings.language.EN ? "contained" : "outlined"
              }
              onClick={() => switchLang(strings.language.EN)}
              sx={{
                color: videoLang === strings.language.EN ? "#fff" : "#184437",
                backgroundColor:
                  videoLang === strings.language.EN ? "#184437" : "transparent",
                borderColor: "#184437",
                "&:hover": {
                  backgroundColor:
                    videoLang === strings.language.EN ? "#123328" : "#f1f6f4",
                  borderColor: "#184437",
                },
              }}
            >
              {strings.video.ENGLISH_VIDEO}
            </Button>
            <Button
              size="small"
              variant={
                videoLang === strings.language.FR ? "contained" : "outlined"
              }
              onClick={() => switchLang(strings.language.FR)}
              sx={{
                color: videoLang === strings.language.FR ? "#fff" : "#184437",
                backgroundColor:
                  videoLang === strings.language.FR ? "#184437" : "transparent",
                borderColor: "#184437",
                "&:hover": {
                  backgroundColor:
                    videoLang === strings.language.FR ? "#123328" : "#f1f6f4",
                  borderColor: "#184437",
                },
              }}
            >
              {strings.video.FRENCH_VIDEO}
            </Button>
          </div>
        )}
        {loading && (
          <div style={{ textAlign: "center", padding: "24px" }}>
            <CircularProgress color="success" />
          </div>
        )}
        {!loading && playUrl && (
          <video
            className="vm-preview-video"
            src={playUrl}
            controls
            autoPlay
            onError={handleVideoError}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} style={{ color: "#184437" }}>
          {strings.words.CANCEL}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

VideoPlayerModal.propTypes = {
  open: PropTypes.bool,
  video: PropTypes.object,
  onClose: PropTypes.func,
};
